import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { DOCUMENT, WINDOW } from '@core/injection-tokens';
import { TabService } from '@dynamic-components/homepage/tab-component/tab.service';
import { Subscription } from 'rxjs';

import { JtpSdkScriptService } from './jtp-sdk-script.service';

@Component({
  selector: 'jb-jtp-sdk-container',
  templateUrl: './jtp-sdk-container.component.html',
  styleUrls: ['./jtp-sdk-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JtpSdkContainerComponent
  implements OnInit, AfterViewChecked, OnDestroy
{
  @Input() contentId: string;
  @Input() contentType: string;
  @Input() minHeight?: {
    desktop?: number;
    tablet?: number;
    mobile?: number;
  };
  @Input() backgroundColor?: string; // hex value
  @HostBinding('style.position') get hostPositionStyle(): string {
    return this.contentType === 'booker' ? 'static' : 'relative';
  }

  subscription: Subscription;

  private isPackagesRendered = false;

  constructor(
    private viewContainerRef: ViewContainerRef,
    private jtpSdkScriptService: JtpSdkScriptService,
    private tabService: TabService,
    @Inject(WINDOW) private window,
    @Inject(DOCUMENT) private document,
  ) {}

  ngOnInit(): void {
    // set css variables from the placeholderHeights so they are accessible in the css media queries
    // variables are scoped to the host element
    if (this.minHeight?.desktop) {
      this.viewContainerRef.element.nativeElement.style.setProperty(
        '--minHeightDesktop',
        this.minHeight?.desktop + 'px',
      );
    }

    if (this.minHeight?.tablet) {
      this.viewContainerRef.element.nativeElement.style.setProperty(
        '--minHeightTablet',
        this.minHeight?.tablet + 'px',
      );
    }

    if (this.minHeight?.mobile) {
      this.viewContainerRef.element.nativeElement.style.setProperty(
        '--minHeightMobile',
        this.minHeight?.mobile + 'px',
      );
    }

    this.jtpSdkScriptService.loadScript().then(this.renderPackages.bind(this));
    this.subscription = this.tabService.onSelectTab((tabIndex: number) => {
      // executes everytime we click a tab
      setTimeout(this.renderPackages.bind(this), 0);
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngAfterViewChecked(): void {
    this.renderPackages();
  }

  private renderPackages() {
    if (
      !this.isPackagesRendered &&
      !!this.window.jtpsdk &&
      this.document.querySelector('#' + this.contentId)
    ) {
      if (this.contentType === 'booker') {
        this.window.jtpsdk.crossSell?.renderBooker({
          type: this.contentId,
          container: '#' + this.contentId,
        });
      } else if (this.contentType === 'marketing') {
        this.window.jtpsdk.crossSell?.renderPackages('#' + this.contentId);
      }
      this.isPackagesRendered = true;
    }
  }
}

/**
 * Convert hex color and opacity to single hex color with alpha value build in
 * @param hexColor Hex value of the color.
 * @param opacity Opacity of the color. Number must be between 0-1 inclusive.
 */
export function toHexWithAlphaColor(
  hexColor: string,
  opacity: number = 1,
): string | null {
  if (!hexColor) {
    return null;
  }

  if (hexColor.charAt(0) !== '#') {
    hexColor = '#' + hexColor;
  }

  // ensure correct color format
  if (hexColor.match(/^#([0-9a-f]){3}$/i)) {
    // convert 3 character hex to 6 character hex by doubling each character
    hexColor = hexColor.replace(
      /#([0-9a-f])([0-9a-f])([0-9a-f])/i,
      '#$1$1$2$2$3$3',
    );
  } else if (!hexColor.match(/^#([0-9a-f]){6}$/i)) {
    return null; // hex color wrong format
  }

  if (opacity === 1) {
    return hexColor; // no opacity, just return;
  } else if (opacity > 1) {
    opacity = 1;
  } else if (opacity < 0) {
    opacity = 0;
  }
  opacity *= 100; // convert opacity too percent
  opacity = Math.round(opacity * (255 / 100)); // convert percentage to rgb whole number
  let hexOpacity = opacity.toString(16); // convert number to base 16

  if (hexOpacity === '0') {
    hexOpacity += '0'; // need two 0's for correct hex color opacity
  }

  return hexColor + hexOpacity.toUpperCase();
}

export const jtpSdkContainerResolver = (registry, data) => {
  let jtpData;

  // Also content id is required
  if (data?.jtpContainer?.[0]?.contentId) {
    jtpData = data?.jtpContainer?.[0];
  }

  return jtpData
    ? {
        contentId: jtpData.contentId,
        contentType: jtpData.contentType,
        minHeight: jtpData.placeholderHeight?.[0] || null,
        backgroundColor: toHexWithAlphaColor(
          jtpData.placeholderColor,
          jtpData.placeholderOpacity,
        ),
      }
    : {};
};
